import { SOL, roomsOn } from '../content/planets';
import type { Carry } from '../sim/simulation';

/**
 * Where the player is in a run, passed between scenes. A run is a tour of Sol: a light-speed
 * transit to each planet in turn, the planet's seeded rooms, and its boss's arena if it has one
 * (the Ranger, on Mars). The very first launch plays the Earth tutorial before the first transit.
 * A rewind starts a new run on a new seed, from the lab.
 */

export interface RunState {
  seed: string;
  /** Which planet of the run (an index into SOL). */
  planet: number;
  room: number;
  rewinds: number;
  /** The Earth tutorial (played once, before the first launch) instead of a planet's room. */
  tutorial?: boolean;
  /** Starlight, armor and mods carried from the previous room or transit (absent at a run's start). */
  carry?: Carry;
}

export type SceneKey = 'Lab' | 'Transit' | 'Play';

/** A fresh run seed: short, so it can be read off the screen and typed into ?seed=. */
export function newSeed(): string {
  return Math.floor(Math.random() * 36 ** 5)
    .toString(36)
    .padStart(5, '0');
}

/** The start of a new run after a rewind or a cleared system. */
export function nextRun(rewinds: number): RunState {
  return { seed: newSeed(), planet: 0, room: 0, rewinds };
}

/**
 * Where leaving a room leads: the planet's next room, the transit to the next planet, or (the
 * system cleared) back to the lab for a new run. The tutorial leads to the first transit with a
 * fresh suit: it ends at the launch.
 */
export function afterRoom(run: RunState, carry: Carry): { scene: SceneKey; run: RunState } {
  if (run.tutorial) {
    return { scene: 'Transit', run: { seed: run.seed, planet: 0, room: 0, rewinds: run.rewinds } };
  }
  const planet = SOL[run.planet];
  if (planet && run.room + 1 < roomsOn(planet)) {
    return { scene: 'Play', run: { ...run, room: run.room + 1, carry } };
  }
  if (run.planet + 1 < SOL.length) {
    return { scene: 'Transit', run: { ...run, planet: run.planet + 1, room: 0, carry } };
  }
  return { scene: 'Lab', run: nextRun(run.rewinds) };
}
